import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import { useDepartmentEntryScreen } from './hooks/useDepartmentEntryScreen'
import { appToast } from '../../../components/AppToast'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

export default function DepartmentMasterEntryScreen() {
  const { state, actions } = useDepartmentEntryScreen()

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8">
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <p className="inline-flex rounded-full bg-secondary-container px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-on-secondary-container">
            Masters Framework
          </p>
          <h1 className="mt-4 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">Department Master</h1>
          <p className="mt-3 text-base leading-relaxed text-on-surface-variant">
            Configure functional units across your organization. Departments serve as the structural anchor for
            workflow assignments and budgetary reporting.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {!state.pendingApproval && (
            <Link
              to="/masters/department/list"
              className={`inline-flex items-center justify-center rounded-md bg-surface-container-high px-8 py-3 text-sm font-semibold text-on-surface-variant ${
                state.isSaving ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              Discard Changes
            </Link>
          )}
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading || Boolean(state.pendingApproval)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-9 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">
              {state.pendingApproval ? 'schedule' : 'send'}
            </span>
            {state.pendingApproval ? 'Submitted for Approval' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </section>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading department details...
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_360px]">
          <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8 xl:p-10">
            <div className="space-y-8">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Department Name <span className="text-error">*</span>
                  </label>
                  <span className="text-[10px] italic text-outline">Required Field</span>
                </div>
                <input
                  type="text"
                  value={state.form.name}
                  onChange={(event) => actions.patchForm('name', event.target.value)}
                  placeholder="e.g., Global Strategic Logistics"
                  className="w-full rounded-md border-0 bg-surface-container-low px-6 py-4 text-2xl text-on-surface outline-none placeholder:text-outline-variant/70"
                />
              </div>

              <label className="grid gap-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  Associated Company <span className="text-error">*</span>
                </span>
                <AppDropdown
                  value={state.form.company_id}
                  onChange={(value) => {
                    actions.patchForm('company_id', value)
                    actions.patchForm('branch_id', '')
                  }}
                  fetchOptions={actions.fetchCompanyOptions}
                  placeholder="Select an Enterprise Company"
                  triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
                  initialLabel={state.initialLabels.company_name}
                />
              </label>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Associated Branch
                  </span>
                  <AppDropdown
                    value={state.form.branch_id}
                    onChange={(value) => actions.patchForm('branch_id', value)}
                    fetchOptions={actions.fetchBranchOptions}
                    placeholder={state.form.company_id ? 'Select Branch (optional)' : 'Select company first'}
                    disabled={!state.form.company_id}
                    triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
                    initialLabel={state.initialLabels.branch_name}
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Department Head
                  </span>
                  <AppDropdown
                    value={state.form.department_head_id}
                    onChange={(value) => actions.patchForm('department_head_id', value)}
                    fetchOptions={actions.fetchHeadOptions}
                    placeholder="Select Department Head (optional)"
                    triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
                    initialLabel={state.initialLabels.department_head_name}
                  />
                </label>
              </div>


              <div className="grid gap-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Operational Status</span>
                <div className="flex gap-3 rounded-md bg-surface-container-low p-1.5">
                  <button
                    type="button"
                    onClick={() => actions.setOperationalStatus('active')}
                    className={`flex-1 rounded-md px-4 py-3 text-base font-bold transition-all ${
                      state.operationalStatus === 'active'
                        ? 'bg-surface-container-lowest text-primary shadow-sm'
                        : 'text-on-surface-variant'
                    }`}
                  >
                    <span className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" />
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.setOperationalStatus('inactive')}
                    className={`flex-1 rounded-md px-4 py-3 text-base font-bold transition-all ${
                      state.operationalStatus === 'inactive'
                        ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                        : 'text-on-surface-variant'
                    }`}
                  >
                    <span className="mr-2 inline-block h-2 w-2 rounded-full bg-outline align-middle" />
                    Inactive
                  </button>
                </div>
              </div>
            </div>
          </article>

          <div className="space-y-6">
            <article className="rounded-[2rem] bg-surface-container-low p-6">
              <h3 className="text-sm font-black uppercase tracking-[0.14em] text-primary">System Context</h3>
              <p className="mt-4 text-base leading-relaxed text-on-surface-variant">
                Establishing a new department will automatically create default ledger accounts in the associated
                company&apos;s financial system.
              </p>

              <div className="mt-5 space-y-3 text-sm text-on-surface">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined mt-0.5 text-base text-primary">info</span>
                  <p>Naming must be unique within the organization hierarchy.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined mt-0.5 text-base text-primary">verified_user</span>
                  <p>Changes are logged for audit compliance ISO 27001.</p>
                </div>
              </div>
            </article>

            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-6">
              <div className="flex items-end justify-between">
                <p className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Onboarding Completion</p>
                <p className="text-2xl font-black text-primary">{state.completionPercent}%</p>
              </div>
              <div className="mt-3 h-2.5 w-full rounded-full bg-surface-variant">
                <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(8, state.completionPercent)}%` }} />
              </div>
              <p className="mt-3 text-xs text-outline">Auto-calculated from filled mandatory and optional form fields.</p>
            </article>

            <article className="grid grid-cols-2 gap-3">
              <button
                type="button"
                className="rounded-xl bg-surface-container-low px-4 py-4 text-left transition-colors hover:bg-surface-container-high"
              >
                <span className="material-symbols-outlined mb-2 block text-primary">file_upload</span>
                <span className="text-xs font-black uppercase tracking-[0.12em] text-on-surface">Import Bulk</span>
              </button>
              <button
                type="button"
                className="rounded-xl bg-surface-container-low px-4 py-4 text-left transition-colors hover:bg-surface-container-high"
              >
                <span className="material-symbols-outlined mb-2 block text-primary">history</span>
                <span className="text-xs font-black uppercase tracking-[0.12em] text-on-surface">View Logs</span>
              </button>
            </article>
          </div>
        </section>
      )}
    </div>
  )
}
